import { i18n } from '@lingui/core'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { H2, H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { NativePlatform, WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { TaskStatusLabel } from '../task-item/TaskStatusLabel'

import type { FC, ReactNode } from 'react'
import type { Task } from '../../types/task.types'

type TaskDetailLayoutStatic = {
  Header: typeof TaskDetailLayoutHeader
  ErrorHeader: typeof TaskDetailLayoutErrorHeader
  Content: typeof TaskDetailLayoutContent
  Footer: typeof TaskDetailLayoutFooter
}

export type Props = {
  /**
   * Content to be displayed
   */
  children?: ReactNode

  className?: string
}

/**
 * Maximum width for the content container
 */

const TaskDetailLayout: FC<Props> & TaskDetailLayoutStatic = ({ children, className }: Props) => {
  return (
    <StContainer className={className}>
      {/* Compound components: Include Content and Footer components */}
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          const { type } = child
          if (
            type === TaskDetailLayoutHeader ||
            type === TaskDetailLayoutErrorHeader ||
            type === TaskDetailLayoutContent ||
            type === TaskDetailLayoutFooter
          ) {
            return child
          }
        }

        return null
      })}
    </StContainer>
  )
}

export const TaskDetailLayoutHeader = ({ task, statusLabel }: { task: Task; statusLabel?: string }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const location = useLocation()

  const renderCareplanChip = () => {
    if (task.type === 'Hylyght') {
      return <StChip type='Primary'>Hylyght</StChip>
    }

    if (task?.careplanDisplay) {
      return <StChip type='Primary'>{task?.careplanDisplay}</StChip>
    }

    return null
  }

  return (
    <StHeader>
      {/* App Header on mobile */}
      <NativePlatform>
        <StMobileHeader>
          <AppHeader
            primaryAction={{
              label: i18n._(globalDefinitions.goBack),
              icon: ['far', 'chevron-left'],
              onClick: () => {
                if (location.state?.from === 'timeline') {
                  navigate(-1)
                } else {
                  navigate('..', {
                    state: { taskId: task.id },
                  })
                }
              },
            }}
            title={task?.display}
            centered={false}
          />
        </StMobileHeader>
      </NativePlatform>

      <StHeaderContent>
        <NativePlatform>
          <StStatusRow>
            <TaskStatusLabel task={task} display={statusLabel} variant='detail' />
            {renderCareplanChip()}
          </StStatusRow>
        </NativePlatform>
        <WebPlatform>
          <FlexColumn gap={theme.UI.SpacingPx.Space4}>
            <H2 noMargin>{task?.display}</H2>

            <StStatusRow>
              {/* Status label */}
              <TaskStatusLabel task={task} display={statusLabel} variant='detail' />
              {/* Careplan Chip */}
              {renderCareplanChip()}
            </StStatusRow>
          </FlexColumn>
        </WebPlatform>
      </StHeaderContent>

      {/* Line */}
      <StLineContainer>
        <StLine />
      </StLineContainer>
    </StHeader>
  )
}

export const TaskDetailLayoutErrorHeader = ({ title }: { title: string }) => {
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <StHeader>
      {/* App Header on mobile */}
      <NativePlatform>
        <StMobileHeader>
          <AppHeader
            primaryAction={{
              label: i18n._(globalDefinitions.goBack),
              icon: ['far', 'chevron-left'],
              onClick: () => {
                if (location.state?.from === 'timeline') {
                  navigate(-1)
                } else {
                  navigate('..')
                }
              },
            }}
            title={title}
          />
        </StMobileHeader>
      </NativePlatform>

      <StHeaderContent>
        <H3 noMargin>{title}</H3>
      </StHeaderContent>

      {/* Line */}
      <StLine />
    </StHeader>
  )
}

export const TaskDetailLayoutContent = ({ children }: Props) => {
  return <StContentContainer>{children}</StContentContainer>
}

export const TaskDetailLayoutFooter = ({ children }: Props) => {
  return (
    <StFooter>
      <StFooterContent>{children}</StFooterContent>
    </StFooter>
  )
}

const StContainer = styled(FlexColumn)`
  flex: 1;
  height: 100%;
  position: relative;
`

const StHeader = styled(FlexColumn)`
  flex-shrink: 0;
  width: 100%;
`

const StHeaderContent = styled(FlexColumn)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space4} ${theme.UI.SpacingPx.Space6}`};
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
  width: 100%;

  margin: 0 auto;
  max-width: 600px;
`

const StLineContainer = styled(FlexColumn)`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;

  padding: 0 ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StLine = styled(Line)`
  border-color: ${({ theme }) => theme.Colors.Neutrals.Light};
  width: 100%;
`

const StContentContainer = styled(FlexColumn)`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space6};
  margin: 0 auto;
  max-width: 600px;
`

const StStatusRow = styled(FlexRow)`
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  row-gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
  width: 100%;

  > *:first-child {
    flex-shrink: 0;
  }
`

const StChip = styled(Chip)`
  flex-shrink: 0;
  flex-wrap: nowrap;
  white-space: nowrap;

  font-size: ${({ theme }) => theme.Typography.BodySmallRegular['font-size']} !important;
  font-weight: ${({ theme }) => theme.Typography.BodySmallRegular['font-weight']} !important;
  line-height: ${({ theme }) => theme.Typography.BodySmallRegular['line-height']} !important;
  letter-spacing: ${({ theme }) => theme.Typography.BodySmallRegular['letter-spacing']} !important;
`

const StMobileHeader = styled.div`
  ${ContentPagePaddingCSS}
  padding-bottom: 0;
`

const StFooter = styled.footer`
  flex-shrink: 1;
  box-shadow: 0px -1px 40px rgba(255, 255, 255, 1);

  background-color: ${({ theme }) => theme.Colors.Background.White};
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4}
    ${({ theme }) => theme.UI.SpacingPx.Space6};
  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), 20px) +
      ${({ theme }) => theme.UI.Spacing.Space1}px
  );
  @media (${breakpoints.fromM}) {
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), 20px) +
        ${({ theme }) => theme.UI.Spacing.Space6}px
    );
  }

  position: sticky;
  align-items: center;

  bottom: 0;
  z-index: 1;
`

const StFooterContent = styled(FlexColumn)`
  max-width: 600px;
  margin: 0 auto;
`

export const StTaskDetailParagraph = styled.p`
  ${({ theme }) => theme.Typography.BodyMediumRegular};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  margin: 0;
`

TaskDetailLayout.Header = TaskDetailLayoutHeader
TaskDetailLayout.ErrorHeader = TaskDetailLayoutErrorHeader
TaskDetailLayout.Content = TaskDetailLayoutContent
TaskDetailLayout.Footer = TaskDetailLayoutFooter

export { TaskDetailLayout }
